import { useEffect, useState, type CSSProperties, type ReactNode, type ChangeEvent } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";

// WEB3 — shared design-language kit for the five ürün teklif-talebi (başvuru) flows.
// Everything here is ported VISUAL-FAITHFUL from the founder's handoff
// docs/design/html-css-export/03-mobil-teklif-trafik.html (fonts Hanken Grotesk +
// Source Serif 4 italic; colors #00249E / #34C77B / #16294C; his input/plate/card/
// button/consent styles). The five product pages compose these primitives so the
// look is identical and there is ONE source of truth for the tokens. Only the
// responsive CHROME (phone bezel on mobile vs a flat wide card on desktop) lives in
// styles.css (.csa-basvuru-*) so a media query can swap it; the founder's field
// tokens stay inline here.

export const BLUE = "#00249E";
export const INK = "#16294C";
export const GREEN_ARROW = "#34C77B";

// The pilot product lines the PUBLIC door accepts (mirrors @csa/validation
// WEB_INTAKE_PRODUCTS). trafik/kasko are VEHICLE lines (asset at the door); the
// rest are LEAD-ONLY.
export type Product = "trafik" | "kasko" | "saglik" | "dask" | "seyahat";

// Same convention as the trafik flow / web-foundation config: read once from Vite's
// import.meta.env, strip a trailing slash. Empty → same-origin relative path.
const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL ?? "").replace(/\/$/, "");
export const INTAKE_URL = `${API_BASE_URL}/v1/public/quote-requests`;
// WEB3 (KARAR-4) — the PUBLIC ruhsat upload (multipart; returns opaque uploadToken).
export const RUHSAT_UPLOAD_URL = `${API_BASE_URL}/v1/public/quote-requests/ruhsat`;
// WEB3 (KARAR-3) — the self-serve "Kayıt ol" registration ceremony (start → confirm).
export const REGISTER_START_URL = `${API_BASE_URL}/v1/public/register/start`;
export const REGISTER_CONFIRM_URL = `${API_BASE_URL}/v1/public/register/confirm`;
// SYSTEM 1 (ADR 0005 §3.1) — the STANDALONE self-serve site-signup (homepage
// "Kayıt ol" → /kayit). A cold visitor with NO TCKN: name/phone/email at start,
// the OTP goes to the SUPPLIED phone, and the account is minted only AFTER it
// verifies at confirm (possession, not knowledge). Uniform responses by design.
export const SELF_REGISTER_START_URL = `${API_BASE_URL}/v1/public/self-register/start`;
export const SELF_REGISTER_CONFIRM_URL = `${API_BASE_URL}/v1/public/self-register/confirm`;

// The customer (party-realm) app lives on its own origin (mirrors Homepage.tsx).
// Used by the "Giriş yap" CTA when register/confirm reports `already_registered`
// (safe to link only AFTER the OTP proved phone possession — ADR 0005 holds).
const CUSTOMER_URL = import.meta.env.VITE_CUSTOMER_URL ?? "http://localhost:5175";

// TR plate — byte-identical to packages/validation asset.ts / web-intake.ts.
export const PLATE_RE = /^[0-9]{2}\s?[A-Z]{1,3}\s?[0-9]{2,4}$/;
export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const digits = (s: string) => s.replace(/\D/g, "");

// ── The exact PUBLIC intake body validated by webIntakeSchema (@csa/validation),
// POSTed to `${VITE_API_BASE_URL}/v1/public/quote-requests` (@Public — no token).
// Field names mirror the DTO 1:1. The schema is .strict(): only these keys are
// accepted. WEB3-B grew person to accept birthDate/il/ilce (all optional) so those
// are now sent; the remaining door fields (ruhsat foto / SGK / adres / UAVT …) are
// still collected in the UI but NOT sent — they land once their backend arrives.
export interface PersonPayload {
  firstName: string;
  lastName: string;
  // WEB3-B: coarse identity/residence now accepted by webIntakeSchema.person
  // (all optional). YYYY-MM-DD for birthDate, per birthDateSchema.
  birthDate?: string;
  il?: string;
  ilce?: string;
  // ORTAK §0 occupation (meslek) — accepted by webIntakeSchema.person (optional);
  // persisted on the party row (0053). Omitted when the user leaves it blank.
  meslek?: string;
  email?: string;
  phone: string;
  tckn: string;
}
export interface VehiclePayload {
  plate?: string;
  label?: string;
  // §1–2 capture-only vehicle/driver attrs (webIntakeSchema.vehicle). ruhsatSerno =
  // tescil belgesi seri no; ehliyetDate = kasko sürücü ehliyet alış tarihi (YYYY-MM-DD).
  ruhsatSerno?: string;
  ehliyetDate?: string;
}
// DASK §3 dwelling block (webIntakeSchema.dwelling, dask line only). grossM2/buildYear
// are z.coerce.number() server-side — sent as numbers here.
export interface DwellingPayload {
  address?: string;
  uavtCode?: string;
  grossM2?: number;
  structure?: "betonarme" | "celik" | "yigma" | "diger";
  buildYear?: number;
  floor?: string;
}
// SEYAHAT §4 trip block (webIntakeSchema.travel, seyahat line only). Dates YYYY-MM-DD.
export interface TravelPayload {
  destination?: string;
  startDate?: string;
  endDate?: string;
  travelers?: number;
}
// SAĞLIK §5 door block (webIntakeSchema.health, saglik line only) — SGK + hospital
// preference + the offline-declaration ACK. The health DECLARATION itself is never here.
export interface HealthPayload {
  sgkStatus?: "sgkli" | "sgksiz" | "bilinmiyor";
  hospitalPref?: string;
  declarationOffline?: boolean;
}
// WEB3 (KARAR-4) — the ruhsat metadata block echoed into the intake body. Mirrors
// webIntakeSchema.ruhsat 1:1: EITHER the opaque uploadToken (+ the authoritative
// content metadata the upload endpoint returned) OR the "telefonda ileteceğim"
// escape (`deferredToPhone`). Never the binary — the bytes travel out-of-band to
// the encrypted-storage endpoint, which mints the token.
export interface RuhsatMeta {
  uploadToken: string;
  contentType: string;
  sizeBytes: number;
  checksumSha256: string;
}
export interface RuhsatDeferred {
  deferredToPhone: true;
}
export type RuhsatBlock = RuhsatMeta | RuhsatDeferred;

export interface IntakeBody {
  product: Product;
  vehicle?: VehiclePayload;
  ruhsat?: RuhsatBlock;
  // Non-motor line blocks — each bound to exactly its line (superRefine): dwelling→dask,
  // travel→seyahat, health→saglik. Sent ONLY for the matching line (no smuggling).
  dwelling?: DwellingPayload;
  travel?: TravelPayload;
  health?: HealthPayload;
  person: PersonPayload;
  consents: {
    aydinlatma: true;
    hizmetKosullari: true;
    mesafeliOnBilgilendirme: true;
    // Special-category (KVKK Art. 6) health açık rıza — saglik line only (superRefine).
    saglikAcikRiza?: true;
  };
}

// ── useIntakeSubmit — POSTs the collected intake to the PUBLIC endpoint. Returns
// true only on the neutral 202 receipt (no ids, no PII — anti-enumeration), false on
// any validation/network error, surfacing a Turkish message. The 202 records a
// lead/quote-request only — it never issues a policy or returns a price
// (advisor-binds / no-instant-price moat preserved).
const RATE_LIMIT_MSG = "Çok fazla deneme yapıldı. Lütfen kısa bir süre sonra tekrar deneyin.";

// Optional per-code Turkish overrides — used only when the server sends no usable
// `detail`. Codes mirror the API's ProblemJson `code` field.
const ERROR_BY_CODE: Record<string, string> = {
  VALIDATION_ERROR: "Gönderdiğiniz bilgilerde bir sorun var. Lütfen kontrol edip tekrar deneyin.",
};

// One entry of the API's ProblemJson `errors` array — a Zod issue: the field
// PATH into webIntakeSchema (e.g. ["person","tckn"]) + its message.
interface ProblemIssue {
  path?: (string | number)[];
  message?: string;
}
interface ProblemJson {
  code?: string;
  detail?: string;
  title?: string;
  // RFC-9457 extension the API sets on a 400 (ProblemJsonFilter passes the Zod
  // issue array straight through). Present ⇒ we can attribute errors to fields.
  errors?: ProblemIssue[];
}

// Boundary field PATH (webIntakeSchema, dotted) → the FE error slot each field
// component reads. Only the fields the flow renders are mapped; anything else
// (product, iysMarketing, unknown) falls through to the generic banner. This is
// INPUT validation only — it leaks nothing about accounts, so it is safe to
// surface specifically at the public step (ADR 0005 anti-enumeration untouched).
const FIELD_KEY_BY_PATH: Record<string, string> = {
  "person.firstName": "firstName",
  "person.lastName": "lastName",
  "person.tckn": "tckn",
  "person.birthDate": "birthDate",
  "person.phone": "phone",
  "person.email": "email",
  "person.il": "il",
  "person.ilce": "ilce",
  "person.meslek": "meslek",
  "consents.aydinlatma": "aydinlatma",
  "consents.hizmetKosullari": "hizmetKosullari",
  "consents.mesafeliOnBilgilendirme": "mesafeliOnBilgilendirme",
  "consents.saglikAcikRiza": "healthConsent",
  vehicle: "plaka",
  "vehicle.plate": "plaka",
  "vehicle.vin": "plaka",
  "vehicle.ehliyetDate": "ehliyetTarihi",
  ruhsat: "ruhsat",
  // Non-motor line fields → their FE input slot (per-product renderInfo field ids).
  "dwelling.address": "adres",
  "dwelling.uavtCode": "uavt",
  "dwelling.grossM2": "m2",
  "dwelling.structure": "yapiTarzi",
  "dwelling.buildYear": "insaYili",
  "dwelling.floor": "kat",
  "travel.destination": "bolge",
  "travel.startDate": "baslangic",
  "travel.endDate": "bitis",
  "travel.travelers": "kisiSayisi",
  "health.sgkStatus": "sgk",
  "health.hospitalPref": "hastane",
};

// Clean Turkish inline copy per field — preferred over the raw (sometimes
// English) boundary message so the user reads a fixable, localized hint.
const FIELD_MSG_TR: Record<string, string> = {
  firstName: "Adınızı girin.",
  lastName: "Soyadınızı girin.",
  tckn: "TC Kimlik No geçersiz — 11 haneli olmalı.",
  birthDate: "Doğum tarihi geçersiz.",
  phone: "Geçerli bir cep telefonu girin.",
  email: "Geçerli bir e-posta girin.",
  il: "İl geçersiz.",
  ilce: "İlçe geçersiz.",
  meslek: "Meslek en fazla 120 karakter olmalı.",
  plaka: "Plaka formatı hatalı (örn. 34 ABC 123).",
  ruhsat: "Ruhsat bilgisi geçersiz.",
  ehliyetTarihi: "Ehliyet alış tarihi geçersiz.",
  aydinlatma: "Aydınlatma metnini onaylayın.",
  hizmetKosullari: "Hizmet koşullarını onaylayın.",
  mesafeliOnBilgilendirme: "Ön bilgilendirme formunu onaylayın.",
  healthConsent: "Sağlık verileriniz için ayrı açık rızanız gerekli.",
  adres: "Adres geçersiz.",
  uavt: "UAVT adres kodu 10 haneli olmalı.",
  m2: "Brüt m² geçersiz.",
  yapiTarzi: "Yapı tarzı geçersiz.",
  insaYili: "İnşa yılı geçersiz.",
  kat: "Kat bilgisi geçersiz.",
  bolge: "Bölge geçersiz.",
  baslangic: "Başlangıç tarihi geçersiz.",
  bitis: "Bitiş tarihi geçersiz.",
  kisiSayisi: "Kişi sayısı geçersiz.",
  sgk: "SGK durumu geçersiz.",
  hastane: "Hastane tercihi geçersiz.",
};

// Maps a ProblemJson body's `errors` (Zod issues) into a { fieldKey: message }
// object the flow merges into its inline-error state. Returns null when the body
// carries no usable field-attributed issue, so the caller keeps the generic
// banner (network / 5xx / opaque 4xx with only a `detail`).
export function fieldErrorsFromProblem(body: ProblemJson): Record<string, string> | null {
  if (!Array.isArray(body.errors)) return null;
  const out: Record<string, string> = {};
  for (const issue of body.errors) {
    const path = Array.isArray(issue?.path) ? issue.path.map(String) : [];
    // Most-specific path first (vehicle.plate), then its head (vehicle).
    const key =
      FIELD_KEY_BY_PATH[path.join(".")] ?? (path[0] ? FIELD_KEY_BY_PATH[path[0]] : undefined);
    if (!key || out[key]) continue;
    out[key] =
      FIELD_MSG_TR[key] ??
      (typeof issue.message === "string" && issue.message.trim() ? issue.message : "Geçersiz.");
  }
  return Object.keys(out).length > 0 ? out : null;
}

// Picks the most specific Turkish message from a parsed ProblemJson body:
// server `detail` → per-code override → title → a status-class fallback.
function pickProblemMessage(body: ProblemJson, status: number): string {
  const detail = typeof body.detail === "string" ? body.detail.trim() : "";
  const title = typeof body.title === "string" ? body.title.trim() : "";
  const byCode = body.code ? ERROR_BY_CODE[body.code] : undefined;
  if (detail) return detail;
  if (byCode) return byCode;
  if (title) return title;
  return status >= 500
    ? "Sunucuda bir hata oluştu. Lütfen daha sonra tekrar deneyin."
    : "Gönderdiğiniz bilgilerde bir sorun var. Lütfen kontrol edip tekrar deneyin.";
}

// Reads the ProblemJson body (best-effort) from a fetch Response.
async function problemMessage(res: Response): Promise<string> {
  let body: ProblemJson = {};
  try {
    body = (await res.json()) as ProblemJson;
  } catch {
    body = {};
  }
  return pickProblemMessage(body, res.status);
}

// XHR sibling of problemMessage — the ruhsat upload uses XMLHttpRequest for upload
// progress events, so it exposes status/responseText rather than a Response.
function xhrProblemMessage(xhr: XMLHttpRequest): string {
  if (xhr.status === 429) return RATE_LIMIT_MSG;
  let body: ProblemJson = {};
  try {
    body = JSON.parse(xhr.responseText) as ProblemJson;
  } catch {
    body = {};
  }
  return pickProblemMessage(body, xhr.status || 500);
}

// Result of an intake submit. `ok:true` → the neutral 202 receipt (advance).
// On failure, `fieldErrors` (when present) carries the specific, field-attributed
// validation messages the flow renders INLINE; otherwise the hook's `error`
// state holds the generic banner copy (network / 5xx / opaque 4xx / rate-limit).
export interface IntakeSubmitOutcome {
  ok: boolean;
  fieldErrors?: Record<string, string>;
}

export function useIntakeSubmit() {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(args: {
    product: Product;
    vehicle?: VehiclePayload;
    ruhsat?: RuhsatBlock;
    dwelling?: DwellingPayload;
    travel?: TravelPayload;
    health?: HealthPayload;
    saglikAcikRiza?: true;
    person: PersonPayload;
  }): Promise<IntakeSubmitOutcome> {
    const body: IntakeBody = {
      product: args.product,
      person: args.person,
      consents: { aydinlatma: true, hizmetKosullari: true, mesafeliOnBilgilendirme: true },
    };
    // Vehicle block is sent ONLY for VEHICLE lines; a non-vehicle line must not
    // smuggle vehicle attributes through the .strict() door (server 400s otherwise).
    if (args.vehicle) body.vehicle = args.vehicle;
    // WEB3 (KARAR-4): the ruhsat metadata/escape (VEHICLE lines only — the schema
    // rejects a ruhsat block on a non-vehicle line).
    if (args.ruhsat) body.ruhsat = args.ruhsat;
    // Per-line non-motor blocks — the caller only supplies the block for the selected
    // line (dwelling→dask, travel→seyahat, health→saglik), so superRefine is satisfied.
    if (args.dwelling) body.dwelling = args.dwelling;
    if (args.travel) body.travel = args.travel;
    if (args.health) body.health = args.health;
    // Special-category health açık rıza — saglik line only (required there; forbidden
    // elsewhere), so the caller passes it only on the saglik line.
    if (args.saglikAcikRiza) body.consents.saglikAcikRiza = true;

    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(INTAKE_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (res.status === 202) return { ok: true };
      if (res.status === 429) {
        setError(RATE_LIMIT_MSG);
        return { ok: false };
      }
      // Read the ProblemJson body once, then decide field-level vs generic. On a
      // 400 the API attaches an `errors` array (Zod issues keyed by field path):
      // attribute each to its field so the user sees the SPECIFIC error inline
      // (TC geçersiz, Plaka formatı hatalı, …) instead of one generic banner.
      let problem: ProblemJson = {};
      try {
        problem = (await res.json()) as ProblemJson;
      } catch {
        problem = {};
      }
      const fieldErrors = fieldErrorsFromProblem(problem);
      if (fieldErrors) return { ok: false, fieldErrors };
      // No usable field errors → keep the generic banner (5xx, or a 4xx that only
      // carried a `detail`/`title` — the existing fallback path).
      setError(pickProblemMessage(problem, res.status));
      return { ok: false };
    } catch {
      // A network/CORS/DNS failure (fetch rejected) — the server was never reached.
      setError("Sunucuya ulaşılamadı — bağlantınızı veya sunucuyu kontrol edin.");
      return { ok: false };
    } finally {
      setSubmitting(false);
    }
  }

  return { submit, submitting, error, setError };
}

// ── useRuhsatUpload — POSTs the ruhsat file (multipart/form-data, one `file`
// field) to the PUBLIC encrypted-storage endpoint and captures the opaque
// uploadToken + authoritative content metadata the server returns (201). Uses
// XMLHttpRequest so the UI can show upload PROGRESS; on failure it surfaces the
// real ProblemJson detail (429/413/415/…) like the intake submit. The metadata
// block is later echoed into the intake body (never the binary).
export type RuhsatUploadStatus = "idle" | "uploading" | "done" | "error";

export function useRuhsatUpload() {
  const [status, setStatus] = useState<RuhsatUploadStatus>("idle");
  const [progress, setProgress] = useState(0);
  const [meta, setMeta] = useState<RuhsatMeta | null>(null);
  const [error, setError] = useState<string | null>(null);

  function reset() {
    setStatus("idle");
    setProgress(0);
    setMeta(null);
    setError(null);
  }

  function upload(file: File) {
    setStatus("uploading");
    setProgress(0);
    setMeta(null);
    setError(null);

    const form = new FormData();
    form.append("file", file);

    const xhr = new XMLHttpRequest();
    xhr.open("POST", RUHSAT_UPLOAD_URL);
    xhr.upload.onprogress = (ev) => {
      if (ev.lengthComputable) setProgress(Math.round((ev.loaded / ev.total) * 100));
    };
    xhr.onload = () => {
      if (xhr.status === 201) {
        try {
          const body = JSON.parse(xhr.responseText) as RuhsatMeta;
          setMeta(body);
          setProgress(100);
          setStatus("done");
        } catch {
          setError("Yükleme yanıtı okunamadı. Lütfen tekrar deneyin.");
          setStatus("error");
        }
        return;
      }
      setError(xhrProblemMessage(xhr));
      setStatus("error");
    };
    xhr.onerror = () => {
      setError("Sunucuya ulaşılamadı — bağlantınızı veya sunucuyu kontrol edin.");
      setStatus("error");
    };
    xhr.send(form);
  }

  return { status, progress, meta, error, upload, reset };
}

// ── useRegister — the self-serve "Kayıt ol" ceremony (KARAR-3). `start(tckn)`
// dispatches the phone-OTP to the party's ON-FILE contact and returns an opaque
// ticket (UNIFORM 200 whether or not the TCKN resolved a party — anti-enumeration);
// `confirm(otp, password)` verifies possession and provisions the account. A
// `{ status: "denied" }` confirm is a UNIFORM deny with no oracle (wrong code OR
// already-registered OR decoy ticket are indistinguishable BY DESIGN); real
// ProblemJson errors (429 rate-limit, 503 provisioning-unavailable, 400 validation)
// still surface their `detail`.
interface RegisterTicket {
  ceremonyId: string;
  registrationTicket: string;
  expiresAt: string;
}

export function useRegister() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ticket, setTicket] = useState<RegisterTicket | null>(null);

  async function start(tckn: string): Promise<boolean> {
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(REGISTER_START_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tckn }),
      });
      if (res.status >= 200 && res.status < 300) {
        setTicket((await res.json()) as RegisterTicket);
        return true;
      }
      setError(res.status === 429 ? RATE_LIMIT_MSG : await problemMessage(res));
      return false;
    } catch {
      setError("Sunucuya ulaşılamadı — bağlantınızı veya sunucuyu kontrol edin.");
      return false;
    } finally {
      setBusy(false);
    }
  }

  async function confirm(
    otp: string,
    password: string,
  ): Promise<"registered" | "denied" | "already_registered" | null> {
    if (!ticket) {
      setError("Kayıt oturumu bulunamadı. Lütfen baştan deneyin.");
      return null;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(REGISTER_CONFIRM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ceremonyId: ticket.ceremonyId,
          registrationTicket: ticket.registrationTicket,
          otp,
          password,
        }),
      });
      if (res.status >= 200 && res.status < 300) {
        const body = (await res.json()) as {
          status: "registered" | "denied" | "already_registered";
        };
        if (body.status === "registered") return "registered";
        // Possession-proven already-exists: the OTP verified, so the server can
        // safely route a returning user to login (ADR 0005 — an attacker without
        // the phone never reaches this). No `error` set — the caller renders a
        // dedicated "Giriş yap" panel rather than a red inline line.
        if (body.status === "already_registered") return "already_registered";
        // Uniform deny — wrong/expired OTP; the server reveals nothing more.
        setError(
          "Kod doğrulanamadı. Kodun süresi dolmuş olabilir. Kodu kontrol edip tekrar deneyin.",
        );
        return "denied";
      }
      setError(res.status === 429 ? RATE_LIMIT_MSG : await problemMessage(res));
      return null;
    } catch {
      setError("Sunucuya ulaşılamadı — bağlantınızı veya sunucuyu kontrol edin.");
      return null;
    } finally {
      setBusy(false);
    }
  }

  return { start, confirm, busy, error, setError, ticket };
}

// ── SYSTEM 1 — sibling of useRegister for the STANDALONE site-signup ceremony
// (/kayit). Same start→confirm shape, but start carries the SUPPLIED identity
// (firstName/lastName/phone/email; NO TCKN) and the OTP is dispatched to the
// supplied phone. Password is collected on screen 1 but held in state and sent
// ONLY at confirm (the backend never persists it between the two calls). Real
// ProblemJson `detail` still surfaces on 429/400/503; a confirm `denied` is a
// UNIFORM deny by design (no oracle). ──────────────────────────────────────────
export interface SelfRegisterStartFields {
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
}

export function useSelfRegister() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ticket, setTicket] = useState<RegisterTicket | null>(null);

  async function start(input: SelfRegisterStartFields): Promise<boolean> {
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(SELF_REGISTER_START_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      });
      if (res.status >= 200 && res.status < 300) {
        setTicket((await res.json()) as RegisterTicket);
        return true;
      }
      setError(res.status === 429 ? RATE_LIMIT_MSG : await problemMessage(res));
      return false;
    } catch {
      setError("Sunucuya ulaşılamadı — bağlantınızı veya sunucuyu kontrol edin.");
      return false;
    } finally {
      setBusy(false);
    }
  }

  async function confirm(
    otp: string,
    password: string,
  ): Promise<"registered" | "denied" | "already_registered" | null> {
    if (!ticket) {
      setError("Kayıt oturumu bulunamadı. Lütfen baştan deneyin.");
      return null;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(SELF_REGISTER_CONFIRM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ceremonyId: ticket.ceremonyId,
          registrationTicket: ticket.registrationTicket,
          otp,
          password,
        }),
      });
      if (res.status >= 200 && res.status < 300) {
        const body = (await res.json()) as {
          status: "registered" | "denied" | "already_registered";
        };
        if (body.status === "registered") return "registered";
        if (body.status === "already_registered") return "already_registered";
        setError(
          "Kod doğrulanamadı. Kodun süresi dolmuş olabilir. Kodu kontrol edip tekrar deneyin.",
        );
        return "denied";
      }
      setError(res.status === 429 ? RATE_LIMIT_MSG : await problemMessage(res));
      return null;
    } catch {
      setError("Sunucuya ulaşılamadı — bağlantınızı veya sunucuyu kontrol edin.");
      return null;
    } finally {
      setBusy(false);
    }
  }

  return { start, confirm, busy, error, setError, ticket };
}

// ── Responsive chrome. Frame = the 03 phone bezel on mobile, a flat wide card on
// desktop (class-driven, see styles.css). ────────────────────────────────────────
export function Frame({ children }: { children: ReactNode }) {
  return (
    <div className="csa-basvuru-frame">
      <div className="csa-basvuru-inner">
        <div className="csa-basvuru-notch" />
        <div className="csa-basvuru-home" />
        {children}
      </div>
    </div>
  );
}

export function StatusBar() {
  return (
    <div className="csa-basvuru-statusbar">
      <span style={{ fontSize: 14, fontWeight: 650, color: "#0C0C10" }}>9:41</span>
      <svg width="24" height="12" viewBox="0 0 26 13" fill="none">
        <rect x="0.5" y="1" width="21" height="11" rx="3" stroke="#0C0C10" strokeOpacity=".4" />
        <rect x="2" y="2.5" width="17" height="8" rx="1.5" fill="#0C0C10" />
      </svg>
    </div>
  );
}

export function Header({
  title,
  step,
  total,
  onBack,
}: {
  title: string;
  step: number;
  total: number;
  onBack: () => void;
}) {
  return (
    <div style={{ background: "#fff", borderBottom: "1px solid #EFF3F6" }}>
      <StatusBar />
      <div
        style={{ height: 50, display: "flex", alignItems: "center", gap: 12, padding: "0 18px" }}
      >
        <button type="button" aria-label="Geri" onClick={onBack} style={backBtn}>
          <ChevronLeft />
        </button>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 15, fontWeight: 650, color: INK }}>{title}</div>
        </div>
        <span style={{ fontSize: 13, fontWeight: 600, color: "#9AA7B4" }}>
          {step}/{total}
        </span>
      </div>
      <div style={{ display: "flex", gap: 5, padding: "0 18px 12px" }}>
        {Array.from({ length: total }, (_, i) => i + 1).map((i) => (
          <span
            key={i}
            style={{
              flex: 1,
              height: 4,
              borderRadius: 2,
              background: i <= step ? BLUE : "#E3E9EF",
            }}
          />
        ))}
      </div>
    </div>
  );
}

export function ScrollBody({ children }: { children: ReactNode }) {
  return <div style={{ flex: 1, overflowY: "auto", padding: "22px 18px" }}>{children}</div>;
}

export function Footer({
  label,
  onClick,
  loading,
}: {
  label: string;
  onClick: () => void;
  loading?: boolean;
}) {
  return (
    <div style={footerWrap}>
      <button
        type="button"
        style={loading ? { ...primaryBtn, opacity: 0.65, cursor: "default" } : primaryBtn}
        onClick={onClick}
        disabled={loading}
        aria-busy={loading}
      >
        {loading ? "Gönderiliyor…" : label}
        {!loading && <ArrowRight />}
      </button>
    </div>
  );
}

export function Intro({ eyebrow, title, text }: { eyebrow: string; title: string; text: string }) {
  return (
    <div className="csa-basvuru-intro">
      <div style={eyebrowStyle}>
        <span style={eyebrowDot} />
        {eyebrow}
      </div>
      <h2 style={introTitle}>{title}</h2>
      <p style={introText}>{text}</p>
      {/* Request-first framing (no-instant-price moat): the web funnel captures a
          REQUEST, never a quote — the advisor prepares the offers afterwards. This
          badge sets that expectation up front, uniformly across all five lines. */}
      <div style={requestBadge} data-testid="request-not-quote-badge">
        <ShieldIcon stroke={BLUE} />
        <span>
          Bu bir teklif değil, <b style={{ color: INK }}>taleptir</b> — anlık fiyat gösterilmez.
        </span>
      </div>
    </div>
  );
}

export function FieldError({ msg }: { msg?: string }) {
  if (!msg) return null;
  return <div style={{ fontSize: 12, color: "#D24A43", marginTop: 6 }}>{msg}</div>;
}

// ── Field primitives (03 tokens). ────────────────────────────────────────────────
export function StepHeading({ title, lead }: { title: string; lead?: string }) {
  return (
    <>
      <h1 style={stepH1}>{title}</h1>
      {lead && <p style={stepLead}>{lead}</p>}
    </>
  );
}

export function TextField({
  id,
  label,
  value,
  onChange,
  error,
  placeholder,
  type = "text",
  inputMode,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  error?: string;
  placeholder?: string;
  type?: string;
  inputMode?: "text" | "numeric" | "tel";
}) {
  return (
    <div style={{ marginBottom: 16 }}>
      <label htmlFor={id} style={fieldLabelSm}>
        {label}
      </label>
      <input
        id={id}
        type={type}
        inputMode={inputMode}
        value={value}
        placeholder={placeholder}
        onChange={(ev) => onChange(ev.target.value)}
        style={textInput}
      />
      <FieldError msg={error} />
    </div>
  );
}

export function SelectField({
  id,
  label,
  value,
  onChange,
  options,
  error,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  error?: string;
}) {
  return (
    <div style={{ marginBottom: 16 }}>
      <label htmlFor={id} style={fieldLabelSm}>
        {label}
      </label>
      <select
        id={id}
        value={value}
        onChange={(ev) => onChange(ev.target.value)}
        style={{ ...textInput, appearance: "none", cursor: "pointer" }}
      >
        <option value="">Seçiniz</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      <FieldError msg={error} />
    </div>
  );
}

// ── Doğum tarihi — birth-date-friendly Gün / Ay / Yıl selects (03 tokens). The
// native <input type="date"> forced users born decades ago to page back from the
// current month; three selects with the YEAR descending from the current year to
// 1920 make reaching e.g. 1981 one scroll. Assembles the DTO's YYYY-MM-DD string
// (birthDateSchema) and stays blank until all three parts are chosen.
const AY_ADLARI = [
  "Ocak",
  "Şubat",
  "Mart",
  "Nisan",
  "Mayıs",
  "Haziran",
  "Temmuz",
  "Ağustos",
  "Eylül",
  "Ekim",
  "Kasım",
  "Aralık",
];
const DOGUM_MIN_YIL = 1920;

export function BirthDatePicker({
  value,
  onChange,
  error,
}: {
  value: string;
  onChange: (v: string) => void;
  error?: string;
}) {
  const currentYear = new Date().getFullYear();
  const [gun, setGun] = useState(value ? String(Number(value.slice(8, 10))) : "");
  const [ay, setAy] = useState(value ? String(Number(value.slice(5, 7))) : "");
  const [yil, setYil] = useState(value ? value.slice(0, 4) : "");

  function commit(g: string, a: string, y: string) {
    setGun(g);
    setAy(a);
    setYil(y);
    onChange(g && a && y ? `${y}-${a.padStart(2, "0")}-${g.padStart(2, "0")}` : "");
  }

  return (
    <div style={{ marginBottom: 16 }}>
      <span style={fieldLabelSm}>Doğum tarihi</span>
      <div style={{ display: "flex", gap: 8 }}>
        <select
          aria-label="Gün"
          value={gun}
          onChange={(ev) => commit(ev.target.value, ay, yil)}
          style={{ ...birthSelect, flex: "0 0 25%" }}
        >
          <option value="">Gün</option>
          {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
            <option key={d} value={d}>
              {d}
            </option>
          ))}
        </select>
        <select
          aria-label="Ay"
          value={ay}
          onChange={(ev) => commit(gun, ev.target.value, yil)}
          style={{ ...birthSelect, flex: 1 }}
        >
          <option value="">Ay</option>
          {AY_ADLARI.map((name, i) => (
            <option key={name} value={i + 1}>
              {name}
            </option>
          ))}
        </select>
        <select
          aria-label="Yıl"
          value={yil}
          onChange={(ev) => commit(gun, ay, ev.target.value)}
          style={{ ...birthSelect, flex: "0 0 30%" }}
        >
          <option value="">Yıl</option>
          {Array.from({ length: currentYear - DOGUM_MIN_YIL + 1 }, (_, i) => currentYear - i).map(
            (y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ),
          )}
        </select>
      </div>
      <FieldError msg={error} />
    </div>
  );
}

// ── SearchableSelect — type-to-filter combobox on the 03 input token. Used for İl
// (81 provinces) so the user types a few letters instead of scanning a long native
// <select>. Commits a value on click OR when the typed text exactly matches an
// option; leaves it blank otherwise so the (required) validation still fires.
export function SearchableSelect({
  id,
  label,
  value,
  onChange,
  options,
  placeholder,
  error,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: readonly string[];
  placeholder?: string;
  error?: string;
}) {
  const [query, setQuery] = useState(value);
  const [open, setOpen] = useState(false);
  const norm = (s: string) => s.toLocaleLowerCase("tr");
  const filtered = options.filter((o) => norm(o).includes(norm(query.trim()))).slice(0, 60);

  function pick(option: string) {
    setQuery(option);
    onChange(option);
    setOpen(false);
  }
  function onType(v: string) {
    setQuery(v);
    setOpen(true);
    const exact = options.find((o) => norm(o) === norm(v.trim()));
    onChange(exact ?? "");
  }

  return (
    <div style={{ marginBottom: 16 }}>
      <label htmlFor={id} style={fieldLabelSm}>
        {label}
      </label>
      <div style={{ position: "relative" }}>
        <input
          id={id}
          value={query}
          placeholder={placeholder}
          autoComplete="off"
          onChange={(ev) => onType(ev.target.value)}
          onFocus={() => setOpen(true)}
          onBlur={() => window.setTimeout(() => setOpen(false), 120)}
          style={textInput}
        />
        {open && filtered.length > 0 && (
          <div style={searchDropdown}>
            {filtered.map((o) => (
              <button
                type="button"
                key={o}
                onMouseDown={(ev) => ev.preventDefault()}
                onClick={() => pick(o)}
                style={searchOption}
              >
                {o}
              </button>
            ))}
          </div>
        )}
      </div>
      <FieldError msg={error} />
    </div>
  );
}

// TR'nin 81 ili — plaka sırasında (01 Adana … 81 Düzce). İl için type-to-filter
// listesi. İlçe seti kasıtlı olarak inline EDİLMEDİ (973 ilçe; elle girmek hem çok
// büyük hem hata riskli) — İlçe serbest metin olarak kalıyor (WEB3-B notu).
export const TURKIYE_ILLERI = [
  "Adana",
  "Adıyaman",
  "Afyonkarahisar",
  "Ağrı",
  "Amasya",
  "Ankara",
  "Antalya",
  "Artvin",
  "Aydın",
  "Balıkesir",
  "Bilecik",
  "Bingöl",
  "Bitlis",
  "Bolu",
  "Burdur",
  "Bursa",
  "Çanakkale",
  "Çankırı",
  "Çorum",
  "Denizli",
  "Diyarbakır",
  "Edirne",
  "Elazığ",
  "Erzincan",
  "Erzurum",
  "Eskişehir",
  "Gaziantep",
  "Giresun",
  "Gümüşhane",
  "Hakkâri",
  "Hatay",
  "Isparta",
  "Mersin",
  "İstanbul",
  "İzmir",
  "Kars",
  "Kastamonu",
  "Kayseri",
  "Kırklareli",
  "Kırşehir",
  "Kocaeli",
  "Konya",
  "Kütahya",
  "Malatya",
  "Manisa",
  "Kahramanmaraş",
  "Mardin",
  "Muğla",
  "Muş",
  "Nevşehir",
  "Niğde",
  "Ordu",
  "Rize",
  "Sakarya",
  "Samsun",
  "Siirt",
  "Sinop",
  "Sivas",
  "Tekirdağ",
  "Tokat",
  "Trabzon",
  "Tunceli",
  "Şanlıurfa",
  "Uşak",
  "Van",
  "Yozgat",
  "Zonguldak",
  "Aksaray",
  "Bayburt",
  "Karaman",
  "Kırıkkale",
  "Batman",
  "Şırnak",
  "Bartın",
  "Ardahan",
  "Iğdır",
  "Yalova",
  "Karabük",
  "Kilis",
  "Osmaniye",
  "Düzce",
] as const;

export function PlateField({
  value,
  onChange,
  error,
}: {
  value: string;
  onChange: (v: string) => void;
  error?: string;
}) {
  return (
    <div style={{ marginBottom: 16 }}>
      <label htmlFor="plaka" style={fieldLabel}>
        Plaka
      </label>
      <div style={plateBox}>
        <span style={plateTr}>
          <TrDot />
          TR
        </span>
        <input
          id="plaka"
          value={value}
          onChange={(ev) => onChange(ev.target.value.toUpperCase())}
          placeholder="34 ABC 123"
          style={plateInput}
        />
      </div>
      <FieldError msg={error} />
    </div>
  );
}

export function PhoneField({
  value,
  onChange,
  error,
}: {
  value: string;
  onChange: (v: string) => void;
  error?: string;
}) {
  return (
    <div style={{ marginBottom: 16 }}>
      <label htmlFor="telefon" style={fieldLabelSm}>
        Cep telefonu
      </label>
      <div style={phoneBox}>
        <span style={phonePrefix}>+90</span>
        <input
          id="telefon"
          inputMode="tel"
          value={value}
          onChange={(ev) => onChange(ev.target.value)}
          placeholder="532 123 45 67"
          style={phoneInput}
        />
      </div>
      <FieldError msg={error} />
    </div>
  );
}

export function TcknField({
  value,
  onChange,
  error,
}: {
  value: string;
  onChange: (v: string) => void;
  error?: string;
}) {
  const [show, setShow] = useState(false);
  return (
    <div style={{ marginBottom: 16 }}>
      <label htmlFor="tckn" style={fieldLabelSm}>
        TC Kimlik No
      </label>
      <div style={{ position: "relative" }}>
        <input
          id="tckn"
          // Self-masking: the user's OWN input on public web, validated/verified
          // server-side (never trusted from the client).
          type={show ? "text" : "password"}
          inputMode="numeric"
          value={value}
          maxLength={11}
          onChange={(ev) => onChange(digits(ev.target.value).slice(0, 11))}
          style={tcknInput}
        />
        <button
          type="button"
          title="Göster / gizle"
          aria-label="TCKN göster / gizle"
          onClick={() => setShow((v) => !v)}
          style={revealBtn}
        >
          <EyeIcon />
        </button>
      </div>
      <div style={maskNote}>
        <LockIcon />
        Maskelenmiş · sunucuda doğrulanır
      </div>
      <FieldError msg={error} />
    </div>
  );
}

// ── Ruhsat (dosya yükle) — ZORUNLU with the "telefonda arandığımda ileteceğim"
// escape (KARAR-4). Ticking the escape makes the upload optional. When a file is
// attached it is POSTed to the encrypted-storage endpoint (see useRuhsatUpload);
// this field renders the live upload status (progress / uploaded ✓ / hata) so the
// user knows the document is safely stored before advancing.
export function RuhsatField({
  file,
  onFile,
  escape,
  onEscape,
  error,
  status = "idle",
  progress = 0,
  uploadError = null,
}: {
  file: File | null;
  onFile: (f: File | null) => void;
  escape: boolean;
  onEscape: (v: boolean) => void;
  error?: string;
  status?: RuhsatUploadStatus;
  progress?: number;
  uploadError?: string | null;
}) {
  function onChange(ev: ChangeEvent<HTMLInputElement>) {
    onFile(ev.target.files && ev.target.files[0] ? ev.target.files[0] : null);
  }
  const boxText =
    status === "uploading"
      ? `Yükleniyor… %${progress}`
      : status === "done"
        ? `${file?.name ?? "Ruhsat"} · yüklendi`
        : file
          ? file.name
          : "Ruhsat fotoğrafını yükleyin";
  const boxColor = status === "done" ? "#1F9D5F" : file ? INK : "#5A6876";
  return (
    <div style={{ marginBottom: 16 }}>
      <label htmlFor="ruhsat" style={fieldLabelSm}>
        Ruhsat (araç tescil belgesi){escape ? "" : " *"}
      </label>
      <label htmlFor="ruhsat" style={{ ...uploadBox, opacity: escape ? 0.55 : 1 }}>
        {status === "done" ? <CheckSmall /> : <UploadIcon />}
        <span style={{ fontSize: 13.5, fontWeight: 600, color: boxColor }}>{boxText}</span>
        <input
          id="ruhsat"
          type="file"
          accept="image/*,application/pdf"
          onChange={onChange}
          disabled={escape}
          style={{ display: "none" }}
        />
      </label>
      {status === "uploading" && (
        <div
          style={{ height: 4, borderRadius: 2, background: "#E3E9EF", marginTop: 8 }}
          role="progressbar"
          aria-label="Ruhsat yükleme ilerlemesi"
        >
          <div
            style={{
              width: `${progress}%`,
              height: "100%",
              borderRadius: 2,
              background: BLUE,
              transition: "width .2s",
            }}
          />
        </div>
      )}
      <label style={{ ...consentRow, paddingTop: 10 }}>
        <input
          type="checkbox"
          checked={escape}
          onChange={(ev) => onEscape(ev.target.checked)}
          style={checkboxInput}
          aria-label="Ruhsatı telefonda ileteceğim"
        />
        <span style={consentText}>Ruhsatı danışmanım telefonda arayınca ileteceğim.</span>
      </label>
      <div style={maskNote}>
        <LockIcon />
        Kimlik belgesi · şifreli saklanır
      </div>
      <FieldError msg={uploadError ?? error} />
    </div>
  );
}

export function ConsentCheckbox({
  checked,
  onChange,
  ariaLabel,
  error,
  children,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  ariaLabel: string;
  error?: string;
  children: ReactNode;
}) {
  return (
    <>
      <label style={consentRow}>
        <input
          type="checkbox"
          checked={checked}
          onChange={(ev) => onChange(ev.target.checked)}
          style={checkboxInput}
          aria-label={ariaLabel}
        />
        <span style={consentText}>{children}</span>
      </label>
      <FieldError msg={error} />
    </>
  );
}

// ── KVKK legal texts. The customer must be able to OPEN and READ what they consent to —
// the old `#aydinlatma`/`#hizmet-kosullari`/`#on-bilgilendirme` anchors pointed at nothing.
// Each link now opens an in-page modal with the text below.
//
// FLAG — PLACEHOLDER DISCIPLINE (mirrors packages/db seed/consent-notices.ts): the TITLE
// (product framing) is final, the legal BODY is a clearly-marked TASLAK until counsel
// delivers the final Turkish texts. The counsel-approved bodies are PARKED (P3 go-live
// gate — consent-notices-v2 carries them pending sign-off); swapping them in here is a copy
// update, never a re-wire. A short plain-language summary is shown so the user reads
// something real, with an explicit "taslak" banner.
export type LegalDocKey = "aydinlatma" | "hizmet-kosullari" | "on-bilgilendirme";
const LEGAL_DOCS: Record<LegalDocKey, { title: string; body: string[] }> = {
  aydinlatma: {
    title: "Aydınlatma Metni",
    body: [
      "Bu metin, 6698 sayılı Kişisel Verilerin Korunması Kanunu (KVKK) kapsamında, kişisel verilerinizin veri sorumlusu CSA Sigorta A.Ş. tarafından hangi amaçlarla işlendiği, kimlere aktarılabileceği ve haklarınız hakkında sizi bilgilendirmek amacıyla hazırlanmaktadır.",
      "Sigorta teklifinizin hazırlanması ve poliçe süreçlerinizin yürütülmesi amacıyla kimlik, iletişim ve poliçeye ilişkin verileriniz işlenir; danışmanınız ve ilgili sigorta şirketleriyle bu amaçla paylaşılabilir.",
    ],
  },
  "hizmet-kosullari": {
    title: "Hizmet Koşulları",
    body: [
      "Bu metin, CSA Sigorta A.Ş. üzerinden sunulan danışman odaklı sigorta hizmetinin kullanım koşullarını düzenler.",
      "Teklif talebiniz, danışman eşleşmeniz ve poliçe süreçlerinize ilişkin karşılıklı hak ve yükümlülükleri kapsar; hizmet, fiyat karşılaştırması değil, danışmanınızın sizin için yürüttüğü bir koruma yönetimidir.",
    ],
  },
  "on-bilgilendirme": {
    title: "Mesafeli Satış Ön Bilgilendirme Formu",
    body: [
      "Bu form, mesafeli olarak sunulan sigorta hizmetine ilişkin; hizmetin niteliği, bedeli, cayma hakkı ve tarafların yükümlülükleri hakkında satış öncesinde sizi bilgilendirmek üzere hazırlanmaktadır.",
      "Teklifiniz onaylanmadan ve poliçeniz düzenlenmeden önce bu bilgiler size ayrıca sunulur.",
    ],
  },
};

const DRAFT_FLAG = "Taslak metin — nihai hukuki metin hazırlanıyor";

// A consent link that OPENS the relevant legal text in a modal (was a dead `href="#…"`
// anchor). Rendered inline, styled to match the founder's consent-link look. Manages its
// own open state so it drops in anywhere a link sat before.
export function ConsentDocLink({ doc, children }: { doc: LegalDocKey; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        style={consentLinkBtn}
        aria-haspopup="dialog"
        data-testid={`consent-link-${doc}`}
        onClick={(e) => {
          e.preventDefault();
          setOpen(true);
        }}
      >
        {children}
      </button>
      {open ? <LegalModal doc={doc} onClose={() => setOpen(false)} /> : null}
    </>
  );
}

// Lightweight modal (no DS dependency existed in web). Portals to document.body so the
// overlay escapes the phone-bezel/card clip; closes on overlay click, the × / Kapat button
// and Escape. aria-modal dialog with the title as its label.
function LegalModal({ doc, onClose }: { doc: LegalDocKey; onClose: () => void }) {
  const d = LEGAL_DOCS[doc];
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return createPortal(
    <div style={modalOverlay} role="presentation" onClick={onClose}>
      <div
        style={modalCard}
        role="dialog"
        aria-modal="true"
        aria-label={d.title}
        data-testid="legal-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div style={modalHead}>
          <h2 style={modalTitle}>{d.title}</h2>
          <button type="button" style={modalClose} aria-label="Kapat" onClick={onClose}>
            ×
          </button>
        </div>
        <div style={modalBodyWrap}>
          <div style={modalDraftFlag} data-testid="legal-modal-draft">
            {DRAFT_FLAG}
          </div>
          {d.body.map((p, i) => (
            <p key={i} style={modalPara}>
              {p}
            </p>
          ))}
        </div>
        <div style={modalFoot}>
          <button type="button" style={modalOkBtn} onClick={onClose}>
            Kapat
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

// ── KVKK — unbundled, unticked required trio, matching webIntakeSchema.consents 1:1
// (each z.literal(true)). Every box is independently required before the intake POST.
export interface ConsentState {
  aydinlatma: boolean;
  hizmetKosullari: boolean;
  mesafeliOnBilgilendirme: boolean;
}
export function KvkkTrio({
  state,
  onChange,
  errors,
  extra,
}: {
  state: ConsentState;
  onChange: (patch: Partial<ConsentState>) => void;
  errors: Record<string, string>;
  extra?: ReactNode;
}) {
  return (
    <div style={kvkkWrap}>
      <div style={kvkkTitle}>Onaylar</div>
      <ConsentCheckbox
        checked={state.aydinlatma}
        onChange={(v) => onChange({ aydinlatma: v })}
        ariaLabel="Aydınlatma metni onayı"
        error={errors.aydinlatma}
      >
        <ConsentDocLink doc="aydinlatma">Aydınlatma Metni</ConsentDocLink>
        ’ni okudum ve anladım.
      </ConsentCheckbox>
      <ConsentCheckbox
        checked={state.hizmetKosullari}
        onChange={(v) => onChange({ hizmetKosullari: v })}
        ariaLabel="Hizmet koşulları onayı"
        error={errors.hizmetKosullari}
      >
        <ConsentDocLink doc="hizmet-kosullari">Hizmet Koşulları</ConsentDocLink>
        ’nı okudum ve kabul ediyorum.
      </ConsentCheckbox>
      <ConsentCheckbox
        checked={state.mesafeliOnBilgilendirme}
        onChange={(v) => onChange({ mesafeliOnBilgilendirme: v })}
        ariaLabel="Mesafeli ön bilgilendirme onayı"
        error={errors.mesafeliOnBilgilendirme}
      >
        <ConsentDocLink doc="on-bilgilendirme">
          Mesafeli Satış Ön Bilgilendirme Formu
        </ConsentDocLink>
        ’nu okudum ve onaylıyorum.
      </ConsentCheckbox>
      {extra}
    </div>
  );
}

export function SubmitError({ msg }: { msg: string | null }) {
  if (!msg) return null;
  return (
    <div role="alert" style={submitErrorBox}>
      {msg}
    </div>
  );
}

// CTO decision (2026-07-28): the "talep referans no" is REMOVED. It was a
// client-side Math.random() string, generated only for display and NEVER sent
// anywhere — the intake POST returns a NEUTRAL 202 with no id (anti-enumeration,
// by design). A reference the customer could quote to an advisor, that no system
// can look up, is a small lie on a trust-brand surface: "söyleyin bu numarayı" →
// advisor finds nothing. A *real* receipt would mean a server-issued opaque token
// on the 202, which changes the frozen public contract (→ ADR) for marginal pilot
// value (the advisor calls the customer today regardless). So the pill is gone;
// the terminal screen already states the honest next step ("bugün sizi arayacak").

// ── A4 — the terminal "Talebiniz alındı" success screen (request-first model). Calm
// and terminal: NO price, NO countdown, NO urgency, NO fake discount. It states what
// happens next (advisor prepares 3–4 offers and calls today), a 3-step "ne olacak"
// list (inceler → 3–4 teklif → sayfanıza + WhatsApp), the client-side talep referans
// no, and that the page may be closed. An OPTIONAL "Kayıt ol" leads to the self-serve
// account (System 2) for those who want to track it themselves; the request is
// already recorded regardless.
export function RequestReceived({
  summaryLabel,
  summaryValue,
  onRegister,
  onHome,
}: {
  summaryLabel: string;
  summaryValue: string;
  onRegister: () => void;
  onHome: () => void;
}) {
  return (
    <div style={{ textAlign: "center" }}>
      <div style={successCircle}>
        <CheckBig />
      </div>
      <h1 style={confTitle}>Talebiniz alındı</h1>
      <p style={confText}>
        Danışmanınız talebinizi inceleyip size özel <b style={{ color: INK }}>3–4 teklif</b>{" "}
        hazırlayacak, önerisini iletecek ve <b style={{ color: INK }}>bugün sizi arayacak</b>. Bu
        bir anlık fiyat değildir; teklifleriniz danışmanınız tarafından hazırlanır.
      </p>

      <div style={stepsCard}>
        <div style={stepsTitle}>Bundan sonra ne olacak?</div>
        <NeOlacakStep n={1} text="Danışmanınız talebinizi inceler." />
        <NeOlacakStep n={2} text="Size özel 3–4 teklif hazırlar." />
        <NeOlacakStep n={3} text="Önerisini sayfanıza ve WhatsApp’tan iletir." last />
      </div>

      <div style={advisorCard}>
        <div style={{ display: "flex", alignItems: "center", gap: 13, marginBottom: 16 }}>
          <span style={esBadgeLg}>EŞ</span>
          <div style={{ flex: 1, textAlign: "left" }}>
            <div style={{ fontSize: 15.5, fontWeight: 650, color: INK }}>Eylem ŞİMŞİROĞLU</div>
            <div style={{ fontSize: 12.5, color: "#7A8896" }}>Kıdemli danışmanınız</div>
          </div>
        </div>
        {/* GAP-AUDIT-2 B1 — bu kart eskiden "Ara" ve "Mesaj" butonları taşıyordu; ikisi de
            handler'sızdı (tıklayınca hiçbir şey olmuyordu) ve modelimizle de çelişiyorlardı:
            talep-önce akışta ARAYAN TARAF BİZİZ. Teslim edemediğimiz kanalları sunmak yerine
            vaadi dürüstçe söylüyoruz. Gerçek bir santral numarası (acente künyesi) geldiğinde
            buraya bir `tel:` bağlantısı eklenebilir. Aynı sebeple "Müsait" rozeti de kaldırıldı:
            danışmanın anlık müsaitliğini hesaplamıyoruz, uydurma durum göstermeyiz. */}
        <p style={advisorNote} data-testid="advisor-callback-note">
          <PhoneIcon />
          Danışmanınız bugün sizi arayacak — sizin aramanıza gerek yok.
        </p>
      </div>

      <div style={{ ...summaryPill, marginBottom: 16 }}>
        <div style={{ textAlign: "left" }}>
          <div style={{ fontSize: 11.5, color: "#5E6D8C", marginBottom: 2 }}>{summaryLabel}</div>
          <div style={{ fontSize: 15, fontWeight: 650, color: INK }}>{summaryValue}</div>
        </div>
        <ShieldIcon stroke={BLUE} />
      </div>

      <p style={closeNote}>Bu sayfayı kapatabilirsiniz.</p>

      <button type="button" style={secondaryBtn} onClick={onRegister}>
        <UserIcon />
        Süreci kendiniz takip etmek için kayıt ol
      </button>
      <button type="button" style={{ ...secondaryBtn, marginBottom: 0 }} onClick={onHome}>
        Ana sayfaya dön
      </button>
    </div>
  );
}

function NeOlacakStep({ n, text, last }: { n: number; text: string; last?: boolean }) {
  return (
    <div style={{ ...stepRow, marginBottom: last ? 0 : 10 }}>
      <span style={stepNum}>{n}</span>
      <span style={{ fontSize: 13.5, lineHeight: 1.45, color: "#4A5765" }}>{text}</span>
    </div>
  );
}

// ── "Kayıt ol" — self-serve account: password + phone-OTP (KARAR-3, real backend).
// Two screens over the register ceremony: ① set a password → `start(tckn)` dispatches
// the OTP to the ON-FILE phone; ② enter the 6-digit code → `confirm(otp, password)`
// provisions the customer-realm account and binds the party. The TCKN is the one
// already collected at step ②; the OTP goes ONLY to the on-file contact (never a
// supplied number), so the party must already exist — the quote-request submit that
// created it ran before this fork. Errors surface the real reason where the contract
// gives one (429/503/400); a confirm "denied" is a UNIFORM deny by design.
export function RegisterOtp({
  phone,
  tckn,
  onHome,
}: {
  phone: string;
  tckn: string;
  onHome: () => void;
}) {
  const [phase, setPhase] = useState<"password" | "otp" | "done" | "already">("password");
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [otp, setOtp] = useState("");
  const [localErr, setLocalErr] = useState<string>();
  const reg = useRegister();

  const maskedPhone = phone ? `+90 ••• ••• ${phone.slice(-2)}` : "telefonunuz";

  async function toOtp() {
    // The realm password policy is authoritative server-side; the boundary schema
    // rejects the obviously weak (min 6). Mirror it so the user is told up front.
    if (password.length < 6) return setLocalErr("Şifre en az 6 karakter olmalı.");
    if (password !== password2) return setLocalErr("Şifreler eşleşmiyor.");
    setLocalErr(undefined);
    reg.setError(null);
    const ok = await reg.start(tckn);
    if (ok) setPhase("otp");
  }
  async function verify() {
    if (digits(otp).length !== 6) return setLocalErr("6 haneli doğrulama kodunu girin.");
    setLocalErr(undefined);
    reg.setError(null);
    const outcome = await reg.confirm(digits(otp), password);
    if (outcome === "registered") setPhase("done");
    // Possession proven, but an account already exists on this phone → route to
    // login instead of the confusing uniform deny (safe post-OTP; ADR 0005).
    else if (outcome === "already_registered") setPhase("already");
  }

  if (phase === "already") {
    return (
      <div style={{ textAlign: "center" }}>
        <h1 style={confTitle}>Zaten hesabınız var</h1>
        <p style={confText}>
          Bu numarayla zaten hesabınız var. Belirlediğiniz şifre ve telefon numaranızla giriş yapın.
        </p>
        <button
          type="button"
          style={{ ...primaryBtn, marginBottom: 12 }}
          onClick={() => {
            window.location.href = CUSTOMER_URL;
          }}
        >
          Giriş yap
        </button>
        <button type="button" style={{ ...secondaryBtn, marginBottom: 0 }} onClick={onHome}>
          Ana sayfaya dön
        </button>
      </div>
    );
  }

  if (phase === "done") {
    return (
      <div style={{ textAlign: "center" }}>
        <div style={successCircle}>
          <CheckBig />
        </div>
        <h1 style={confTitle}>Hesabınız oluşturuldu</h1>
        <p style={confText}>
          Telefonunuz doğrulandı ve hesabınız hazır. Belirlediğiniz şifre ve telefon numaranızla
          giriş yaparak sürecinizi kendiniz takip edebilirsiniz.
        </p>
        <button type="button" style={{ ...secondaryBtn, marginBottom: 0 }} onClick={onHome}>
          Ana sayfaya dön
        </button>
      </div>
    );
  }

  const shownErr = localErr ?? reg.error ?? undefined;

  return (
    <div>
      <StepHeading
        title={phase === "password" ? "Hesabınızı oluşturun" : "Telefonunuzu doğrulayın"}
        lead={
          phase === "password"
            ? "Süreci kendiniz takip etmek için bir şifre belirleyin."
            : `${maskedPhone} numarasına gönderdiğimiz 6 haneli kodu girin.`
        }
      />
      {phase === "password" ? (
        <>
          <TextField
            id="sifre"
            label="Şifre"
            type="password"
            value={password}
            onChange={setPassword}
            placeholder="En az 6 karakter"
          />
          <TextField
            id="sifre2"
            label="Şifre (tekrar)"
            type="password"
            value={password2}
            onChange={setPassword2}
          />
          <FieldError msg={shownErr} />
          <button
            type="button"
            style={
              reg.busy
                ? { ...primaryBtn, marginTop: 16, opacity: 0.65, cursor: "default" }
                : { ...primaryBtn, marginTop: 16 }
            }
            onClick={() => void toOtp()}
            disabled={reg.busy}
            aria-busy={reg.busy}
          >
            {reg.busy ? "Kod gönderiliyor…" : "Doğrulama kodu gönder"}
          </button>
        </>
      ) : (
        <>
          <TextField
            id="otp"
            label="Doğrulama kodu"
            inputMode="numeric"
            value={otp}
            onChange={(v) => setOtp(digits(v).slice(0, 6))}
            placeholder="000000"
          />
          <FieldError msg={shownErr} />
          <button
            type="button"
            style={
              reg.busy
                ? { ...primaryBtn, marginTop: 16, opacity: 0.65, cursor: "default" }
                : { ...primaryBtn, marginTop: 16 }
            }
            onClick={() => void verify()}
            disabled={reg.busy}
            aria-busy={reg.busy}
          >
            {reg.busy ? "Doğrulanıyor…" : "Doğrula ve hesabı oluştur"}
          </button>
        </>
      )}
    </div>
  );
}

// ── SYSTEM 1 — the STANDALONE self-serve "Kayıt ol" page (/kayit). Two screens
// over the site-signup ceremony, reusing the funnel chrome + field tokens so the
// look never forks from RegisterOtp:
//   ① İsim/Soyisim/Cep(+90)/E-posta/Şifre(+tekrar) + KVKK aydınlatma onayı →
//     useSelfRegister.start dispatches the OTP to the SUPPLIED phone.
//   ② 6-hane OTP → confirm(otp, password) — the password is held in state from
//     screen ① and sent ONLY here (matches the backend: never persisted between
//     start and confirm). `registered` → hesap oluşturuldu + Giriş yap;
//     `already_registered` → zaten hesabınız var + Giriş yap; `denied` → uniform
//     retry. The ceremonyId/registrationTicket live in the hook's ticket state.
export function SelfRegisterFlow() {
  const navigate = useNavigate();
  const [phase, setPhase] = useState<"form" | "otp" | "done" | "already">("form");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [consent, setConsent] = useState(false);
  const [otp, setOtp] = useState("");
  const [localErr, setLocalErr] = useState<string>();
  const reg = useSelfRegister();

  const maskedPhone = digits(phone) ? `+90 ••• ••• ${digits(phone).slice(-2)}` : "telefonunuz";
  const goHome = () => navigate("/");
  const goLogin = () => {
    window.location.href = CUSTOMER_URL;
  };

  async function submitForm() {
    if (!firstName.trim()) return setLocalErr("Adınızı girin.");
    if (!lastName.trim()) return setLocalErr("Soyadınızı girin.");
    if (digits(phone).length < 10) return setLocalErr("Geçerli bir cep telefonu girin.");
    if (!EMAIL_RE.test(email.trim())) return setLocalErr("Geçerli bir e-posta girin.");
    // The realm password policy is authoritative server-side; the boundary schema
    // rejects the obviously weak (min 6). Mirror it so the user is told up front.
    if (password.length < 6) return setLocalErr("Şifre en az 6 karakter olmalı.");
    if (password !== password2) return setLocalErr("Şifreler eşleşmiyor.");
    if (!consent) return setLocalErr("Devam etmek için aydınlatma metnini onaylayın.");
    setLocalErr(undefined);
    reg.setError(null);
    const ok = await reg.start({
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      // TR-normalized server-side (same pipe as the web door); we send E.164-ish.
      phone: `+90${digits(phone)}`,
      email: email.trim(),
    });
    if (ok) setPhase("otp");
  }

  async function verify() {
    if (digits(otp).length !== 6) return setLocalErr("6 haneli doğrulama kodunu girin.");
    setLocalErr(undefined);
    reg.setError(null);
    const outcome = await reg.confirm(digits(otp), password);
    if (outcome === "registered") setPhase("done");
    else if (outcome === "already_registered") setPhase("already");
  }

  const shownErr = localErr ?? reg.error ?? undefined;

  return (
    <main className="csa-basvuru-page">
      <div className="csa-basvuru-shell">
        <Intro
          eyebrow="HESAP · KAYIT OL"
          title="Hesabınızı oluşturun"
          text="Teklif formu doldurmadan hemen bir hesap açın. Bilgilerinizi girin, telefonunuza gelen kodla doğrulayın; giriş yapıp teklif almaya başlayın."
        />

        <Frame>
          {phase === "form" || phase === "otp" ? (
            <Header
              title="Kayıt ol"
              step={phase === "form" ? 1 : 2}
              total={2}
              onBack={phase === "form" ? goHome : () => setPhase("form")}
            />
          ) : (
            <StatusBar />
          )}

          {phase === "form" && (
            <>
              <ScrollBody>
                <StepHeading
                  title="Bilgileriniz"
                  lead="Ad, soyad, telefon ve e-postanızla başlayın."
                />
                <TextField id="ad" label="İsim" value={firstName} onChange={setFirstName} />
                <TextField id="soyad" label="Soyisim" value={lastName} onChange={setLastName} />
                <PhoneField value={phone} onChange={setPhone} />
                <TextField
                  id="email"
                  label="E-posta"
                  type="email"
                  value={email}
                  onChange={setEmail}
                  placeholder="ornek@eposta.com"
                />
                <TextField
                  id="sifre"
                  label="Şifre"
                  type="password"
                  value={password}
                  onChange={setPassword}
                  placeholder="En az 6 karakter"
                />
                <TextField
                  id="sifre2"
                  label="Şifre (tekrar)"
                  type="password"
                  value={password2}
                  onChange={setPassword2}
                />
                <div style={kvkkWrap}>
                  <div style={kvkkTitle}>Onay</div>
                  <ConsentCheckbox
                    checked={consent}
                    onChange={setConsent}
                    ariaLabel="Aydınlatma metni onayı"
                  >
                    <ConsentDocLink doc="aydinlatma">Aydınlatma Metni</ConsentDocLink>
                    ’ni okudum ve anladım.
                  </ConsentCheckbox>
                </div>
                <FieldError msg={shownErr} />
                <button
                  type="button"
                  style={selfRegisterLoginLink}
                  onClick={goLogin}
                  aria-label="Zaten hesabın var mı? Giriş yap"
                >
                  Zaten hesabın var mı?{" "}
                  <span style={{ color: BLUE, fontWeight: 650 }}>Giriş yap</span>
                </button>
              </ScrollBody>
              <Footer
                label="Doğrulama kodu gönder"
                onClick={() => void submitForm()}
                loading={reg.busy}
              />
            </>
          )}

          {phase === "otp" && (
            <>
              <ScrollBody>
                <StepHeading
                  title="Telefonunuzu doğrulayın"
                  lead={`${maskedPhone} numarasına gönderdiğimiz 6 haneli kodu girin.`}
                />
                <TextField
                  id="otp"
                  label="Doğrulama kodu"
                  inputMode="numeric"
                  value={otp}
                  onChange={(v) => setOtp(digits(v).slice(0, 6))}
                  placeholder="000000"
                />
                <FieldError msg={shownErr} />
              </ScrollBody>
              <Footer
                label="Doğrula ve hesabı oluştur"
                onClick={() => void verify()}
                loading={reg.busy}
              />
            </>
          )}

          {phase === "done" && (
            <div style={{ flex: 1, overflowY: "auto", padding: "30px 22px", textAlign: "center" }}>
              <div style={successCircle}>
                <CheckBig />
              </div>
              <h1 style={confTitle}>Hesabınız oluşturuldu</h1>
              <p style={confText}>
                Telefonunuz doğrulandı ve hesabınız hazır. Giriş yapıp teklif alabilirsiniz.
              </p>
              <button type="button" style={{ ...primaryBtn, marginBottom: 12 }} onClick={goLogin}>
                Giriş yap
              </button>
              <button type="button" style={{ ...secondaryBtn, marginBottom: 0 }} onClick={goHome}>
                Ana sayfaya dön
              </button>
            </div>
          )}

          {phase === "already" && (
            <div style={{ flex: 1, overflowY: "auto", padding: "30px 22px", textAlign: "center" }}>
              <h1 style={confTitle}>Zaten hesabınız var</h1>
              <p style={confText}>
                Bu numarayla zaten hesabınız var. Telefon numaranız ve şifrenizle giriş yapın.
              </p>
              <button type="button" style={{ ...primaryBtn, marginBottom: 12 }} onClick={goLogin}>
                Giriş yap
              </button>
              <button type="button" style={{ ...secondaryBtn, marginBottom: 0 }} onClick={goHome}>
                Ana sayfaya dön
              </button>
            </div>
          )}
        </Frame>
      </div>
    </main>
  );
}

// ── Icons (verbatim paths from 03, plus a few new line-icons in the same idiom). ──
export function ChevronLeft() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke={INK}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="m15 18-6-6 6-6" />
    </svg>
  );
}
export function ArrowRight() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke={GREEN_ARROW}
      strokeWidth="2.3"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M5 12h14" />
      <path d="m12 5 7 7-7 7" />
    </svg>
  );
}
export function ShieldIcon({ stroke }: { stroke: string }) {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke={stroke}
      strokeWidth="1.9"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" />
      <path d="m9 12 2 2 4-4" />
    </svg>
  );
}
function CheckBig() {
  return (
    <svg
      width="40"
      height="40"
      viewBox="0 0 24 24"
      fill="none"
      stroke="#1F9D5F"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}
function CheckSmall() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="#1F9D5F"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ flex: "0 0 auto" }}
    >
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}
function TrDot() {
  return (
    <svg width="13" height="9" viewBox="0 0 16 11" fill="#fff">
      <circle cx="8" cy="3" r="1.4" />
    </svg>
  );
}
export function InfoNote({ children }: { children: ReactNode }) {
  return (
    <div style={infoNote}>
      <InfoIcon />
      <span style={{ fontSize: 12.5, lineHeight: 1.5, color: "#2C5A8A" }}>{children}</span>
    </div>
  );
}
function InfoIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="#2B6CB0"
      strokeWidth="1.9"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ flex: "0 0 auto", marginTop: 1 }}
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M12 16v-4" />
      <path d="M12 8h.01" />
    </svg>
  );
}
function EyeIcon() {
  return (
    <svg
      width="19"
      height="19"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.9"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}
function LockIcon() {
  return (
    <svg
      width="11"
      height="11"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.9"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="3" y="11" width="18" height="11" rx="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}
function PhoneIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="#fff"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M13.8 16.6a1 1 0 0 0 1.2-.3l.4-.5a2 2 0 0 1 1.6-.8h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2A18 18 0 0 1 2 4a2 2 0 0 1 2-2h3a2 2 0 0 1 2 2v3a2 2 0 0 1-.8 1.6l-.5.4a1 1 0 0 0-.3 1.2 14 14 0 0 0 6.4 6.4z" />
    </svg>
  );
}
function UploadIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke={BLUE}
      strokeWidth="1.9"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ flex: "0 0 auto" }}
    >
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <path d="M17 8l-5-5-5 5" />
      <path d="M12 3v12" />
    </svg>
  );
}
function UserIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke={BLUE}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  );
}

// ── Styles (values verbatim from 03). ────────────────────────────────────────────
const eyebrowStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  color: "#1F9D5F",
  marginBottom: 12,
};
const eyebrowDot: CSSProperties = {
  width: 7,
  height: 7,
  borderRadius: "50%",
  background: "#34C77B",
};
const introTitle: CSSProperties = {
  fontSize: 28,
  fontWeight: 700,
  letterSpacing: "-0.02em",
  color: INK,
  margin: "0 0 10px",
};
const introText: CSSProperties = { fontSize: 15, lineHeight: 1.6, color: "#6B7A89", margin: 0 };
const backBtn: CSSProperties = {
  width: 38,
  height: 38,
  borderRadius: 11,
  background: "#F2F5F8",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  border: "none",
  cursor: "pointer",
  padding: 0,
};
const footerWrap: CSSProperties = {
  background: "#fff",
  borderTop: "1px solid #EFF3F6",
  padding: "14px 18px 26px",
};
const primaryBtn: CSSProperties = {
  width: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 8,
  background: BLUE,
  color: "#fff",
  border: "none",
  borderRadius: 15,
  padding: 17,
  fontSize: 16,
  fontWeight: 650,
  cursor: "pointer",
  boxShadow: "rgba(0, 36, 158, 0.5) 0px 12px 24px -12px",
};
const secondaryBtn: CSSProperties = {
  width: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 8,
  background: "#fff",
  color: BLUE,
  border: "1.5px solid #D7DEEA",
  borderRadius: 15,
  padding: 16,
  fontSize: 15,
  fontWeight: 650,
  cursor: "pointer",
  marginBottom: 12,
};
const stepH1: CSSProperties = {
  fontSize: 23,
  fontWeight: 700,
  letterSpacing: "-0.02em",
  color: INK,
  margin: "0 0 6px",
  lineHeight: 1.2,
};
const stepLead: CSSProperties = { fontSize: 13.5, color: "#7A8896", margin: "0 0 22px" };
const selfRegisterLoginLink: CSSProperties = {
  display: "block",
  width: "100%",
  marginTop: 18,
  background: "none",
  border: "none",
  textAlign: "center",
  fontSize: 13.5,
  color: "#7A8896",
  cursor: "pointer",
};
const confTitle: CSSProperties = {
  fontSize: 24,
  fontWeight: 700,
  letterSpacing: "-0.02em",
  color: INK,
  margin: "0 0 8px",
};
const confText: CSSProperties = {
  fontSize: 14,
  lineHeight: 1.6,
  color: "#6B7A89",
  margin: "0 0 24px",
};
const fieldLabel: CSSProperties = {
  display: "block",
  fontSize: 13,
  fontWeight: 600,
  color: "#3A4A59",
  marginBottom: 8,
};
const fieldLabelSm: CSSProperties = {
  display: "block",
  fontSize: 13,
  fontWeight: 600,
  color: "#3A4A59",
  marginBottom: 7,
};
const textInput: CSSProperties = {
  width: "100%",
  boxSizing: "border-box",
  border: "1.5px solid #DCE3E9",
  borderRadius: 13,
  outline: "none",
  fontFamily: "inherit",
  fontSize: 15,
  color: INK,
  padding: "15px 16px",
  background: "#fff",
};
const birthSelect: CSSProperties = {
  ...textInput,
  appearance: "none",
  cursor: "pointer",
  padding: "15px 12px",
};
const searchDropdown: CSSProperties = {
  position: "absolute",
  top: "calc(100% + 4px)",
  left: 0,
  right: 0,
  background: "#fff",
  border: "1.5px solid #DCE3E9",
  borderRadius: 13,
  boxShadow: "rgba(16, 41, 76, 0.14) 0px 12px 26px -12px",
  maxHeight: 220,
  overflowY: "auto",
  zIndex: 20,
  padding: 6,
};
const searchOption: CSSProperties = {
  display: "block",
  width: "100%",
  textAlign: "left",
  background: "transparent",
  border: "none",
  borderRadius: 9,
  padding: "11px 12px",
  fontFamily: "inherit",
  fontSize: 14.5,
  color: INK,
  cursor: "pointer",
};
const plateBox: CSSProperties = {
  display: "flex",
  alignItems: "stretch",
  background: "#fff",
  border: `2px solid ${BLUE}`,
  borderRadius: 13,
  overflow: "hidden",
  boxShadow: "rgba(0, 36, 158, 0.08) 0px 0px 0px 4px",
};
const plateTr: CSSProperties = {
  width: 34,
  background: BLUE,
  color: "#fff",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  fontSize: 9,
  fontWeight: 700,
  gap: 2,
};
const plateInput: CSSProperties = {
  flex: 1,
  border: "none",
  outline: "none",
  fontFamily: "inherit",
  fontSize: 19,
  fontWeight: 700,
  letterSpacing: "0.06em",
  color: INK,
  padding: "15px 14px",
  background: "#fff",
  width: "100%",
};
const infoNote: CSSProperties = {
  display: "flex",
  alignItems: "flex-start",
  gap: 10,
  background: "#EAF2FB",
  borderRadius: 13,
  padding: "13px 14px",
  marginBottom: 4,
};
const tcknInput: CSSProperties = {
  width: "100%",
  boxSizing: "border-box",
  border: "1.5px solid #DCE3E9",
  borderRadius: 13,
  outline: "none",
  fontFamily: "inherit",
  fontSize: 15,
  letterSpacing: "0.08em",
  color: INK,
  padding: "15px 46px 15px 16px",
  background: "#fff",
};
const revealBtn: CSSProperties = {
  position: "absolute",
  right: 10,
  top: "50%",
  transform: "translateY(-50%)",
  width: 32,
  height: 32,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "transparent",
  border: "none",
  borderRadius: 8,
  cursor: "pointer",
  color: "#7A8896",
};
const maskNote: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 5,
  fontSize: 11,
  color: "#9AA7B4",
  marginTop: 6,
};
const uploadBox: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  border: `1.5px dashed ${BLUE}`,
  borderRadius: 13,
  padding: "15px 16px",
  background: "#F7F9FF",
  cursor: "pointer",
};
const phoneBox: CSSProperties = {
  display: "flex",
  alignItems: "center",
  border: `2px solid ${BLUE}`,
  borderRadius: 13,
  overflow: "hidden",
  boxShadow: "rgba(0, 36, 158, 0.08) 0px 0px 0px 4px",
  background: "#fff",
};
const phonePrefix: CSSProperties = {
  fontSize: 15,
  fontWeight: 600,
  color: "#5A6876",
  padding: "15px 12px 15px 16px",
  borderRight: "1px solid #E6ECF1",
};
const phoneInput: CSSProperties = {
  flex: 1,
  border: "none",
  outline: "none",
  fontFamily: "inherit",
  fontSize: 15,
  color: INK,
  padding: "15px 14px",
  background: "#fff",
  width: "100%",
};
const kvkkWrap: CSSProperties = {
  background: "#F7F9FB",
  border: "1px solid #EEF2F5",
  borderRadius: 14,
  padding: "14px 14px 4px",
  marginTop: 3,
};
const kvkkTitle: CSSProperties = {
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: "#7A8896",
  marginBottom: 10,
};
const consentRow: CSSProperties = {
  display: "flex",
  alignItems: "flex-start",
  gap: 11,
  padding: "2px 0 10px",
  cursor: "pointer",
};
const consentText: CSSProperties = { fontSize: 12, lineHeight: 1.5, color: "#7A8896" };
const consentLink: CSSProperties = {
  color: BLUE,
  fontWeight: 600,
  textDecoration: "none",
  borderBottom: "1px solid #C3CDF0",
};
// The consent link as a real <button> (opens the modal) — reset the native button chrome
// but keep the founder's link look (spread consentLink, then re-assert the underline the
// `border: none` reset would clear).
const consentLinkBtn: CSSProperties = {
  ...consentLink,
  display: "inline",
  padding: 0,
  margin: 0,
  background: "none",
  border: "none",
  borderBottom: "1px solid #C3CDF0",
  font: "inherit",
  cursor: "pointer",
};
const modalOverlay: CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(11, 28, 44, 0.5)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 20,
  zIndex: 1000,
};
const modalCard: CSSProperties = {
  background: "#fff",
  borderRadius: 16,
  width: "100%",
  maxWidth: 460,
  maxHeight: "85vh",
  display: "flex",
  flexDirection: "column",
  boxShadow: "rgba(11, 28, 44, 0.25) 0px 20px 50px -12px",
  overflow: "hidden",
};
const modalHead: CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 12,
  padding: "16px 18px",
  borderBottom: "1px solid #EAEFF3",
};
const modalTitle: CSSProperties = {
  fontSize: 16,
  fontWeight: 700,
  color: INK,
  margin: 0,
};
const modalClose: CSSProperties = {
  background: "none",
  border: "none",
  fontSize: 22,
  lineHeight: 1,
  color: "#7A8896",
  cursor: "pointer",
  padding: 4,
};
const modalBodyWrap: CSSProperties = {
  padding: "16px 18px",
  overflowY: "auto",
};
const modalDraftFlag: CSSProperties = {
  fontSize: 11.5,
  fontWeight: 700,
  color: "#8A6D00",
  background: "#FDF6E3",
  border: "1px solid #F1E2B8",
  borderRadius: 8,
  padding: "8px 10px",
  marginBottom: 12,
};
const modalPara: CSSProperties = {
  fontSize: 13,
  lineHeight: 1.6,
  color: "#4A5765",
  margin: "0 0 10px",
};
const modalFoot: CSSProperties = {
  display: "flex",
  justifyContent: "flex-end",
  padding: "12px 18px",
  borderTop: "1px solid #EAEFF3",
};
const modalOkBtn: CSSProperties = {
  background: BLUE,
  color: "#fff",
  border: "none",
  borderRadius: 10,
  padding: "9px 18px",
  fontSize: 13.5,
  fontWeight: 600,
  cursor: "pointer",
};
const checkboxInput: CSSProperties = {
  width: 18,
  height: 18,
  flex: "0 0 auto",
  marginTop: 1,
  accentColor: BLUE,
};
const submitErrorBox: CSSProperties = {
  marginTop: 14,
  background: "#FCECEB",
  border: "1px solid #F2C9C6",
  borderRadius: 12,
  padding: "12px 14px",
  fontSize: 12.5,
  lineHeight: 1.5,
  color: "#B23B34",
};
// Request-first framing badge (Intro) + the A4 terminal's referans/steps/close-note.
const requestBadge: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 9,
  background: "#EAEDFA",
  border: "1px solid #D7DEEA",
  borderRadius: 12,
  padding: "11px 13px",
  marginTop: 14,
  fontSize: 12.5,
  lineHeight: 1.45,
  color: "#3A4A59",
};
const stepsCard: CSSProperties = {
  background: "#F7F9FB",
  border: "1px solid #EEF2F5",
  borderRadius: 18,
  padding: 16,
  textAlign: "left",
  marginBottom: 16,
};
const stepsTitle: CSSProperties = {
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: "#7A8896",
  marginBottom: 12,
};
const stepRow: CSSProperties = {
  display: "flex",
  alignItems: "flex-start",
  gap: 11,
};
const stepNum: CSSProperties = {
  width: 24,
  height: 24,
  borderRadius: "50%",
  background: BLUE,
  color: "#fff",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: 12,
  fontWeight: 700,
  flex: "0 0 auto",
};
const closeNote: CSSProperties = {
  fontSize: 12.5,
  color: "#9AA7B4",
  margin: "0 0 16px",
};
const successCircle: CSSProperties = {
  width: 78,
  height: 78,
  borderRadius: "50%",
  background: "#EFFBF4",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  margin: "14px auto 20px",
  boxShadow: "rgba(52, 199, 123, 0.1) 0px 0px 0px 8px",
};
const advisorCard: CSSProperties = {
  background: "#F7F9FB",
  border: "1px solid #EEF2F5",
  borderRadius: 18,
  padding: 18,
  textAlign: "left",
  marginBottom: 16,
};
const esBadgeLg: CSSProperties = {
  width: 52,
  height: 52,
  borderRadius: "50%",
  background: BLUE,
  color: "#fff",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: 16,
  fontWeight: 700,
  flex: "0 0 auto",
};
// GAP-AUDIT-2 B1 — availPill / advisorBtnPrimary / advisorBtnGhost kaldırıldı:
// taşıdıkları kontroller (Müsait rozeti + Ara/Mesaj butonları) ya uydurma durum
// gösteriyordu ya da handler'sızdı. Yerine tek, dürüst bir geri-arama notu geldi.
const advisorNote: CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 8,
  margin: 0,
  color: "#5E6D8C",
  fontSize: 13.5,
  lineHeight: 1.5,
  textAlign: "center",
};
const summaryPill: CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  background: "#EAEDFA",
  borderRadius: 14,
  padding: "14px 16px",
};
